import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './Signals.module.css';
import SignalCard from '../../components/strategies/SignalCard/SignalCard';
import Spinner from '../../components/common/Spinner/Spinner';
import { useSignals } from '../../hooks/useSignals';
import Performance from '../../components/strategies/Performance/Performance';
import { strategyEngineService } from '../../services/strategyEngineService';

const Signals = () => {
  const {
    signals, total, loading, error, isAdmin,
    fetchSignals, clearError,
  } = useSignals();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mine = searchParams.get('mine') === 'true';

  const [page, setPage] = useState(1);
  const limit = 20;
  const [tab, setTab] = useState('feed');

  const [filters, setFilters] = useState({
    symbol: '',
    action_type: '',
    status: '',
    signal_type: '',
  });

  // Reflects whether live market data is flowing (not just socket open).
  const [feedLive, setFeedLive] = useState(false);
  const [feedReachable, setFeedReachable] = useState(false);

  useEffect(() => {
    const allFilters = { ...filters, ...(mine ? { mine: 'true' } : {}) };
    fetchSignals(page, limit, allFilters).catch(() => {});
  }, [page, limit, filters, mine, fetchSignals]);

  useEffect(() => {
    let active = true;
    const poll = () => {
      strategyEngineService.getFeedStatus()
        .then((res) => {
          if (!active) return;
          setFeedReachable(true);
          setFeedLive(!!(res.data && res.data.isLive));
        })
        .catch(() => {
          if (!active) return;
          setFeedReachable(false);
          setFeedLive(false);
        });
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);
  const feedStatus = !feedReachable ? 'disconnected' : feedLive ? 'live' : 'idle';

  return (
    <div className={styles.page}>
      {/* Back button — offset to match StrategyDetail / BacktestPage */}
      <div className={styles.backRow}>
        <button className={styles.backBtn} onClick={() => navigate('/strategies')}>
          <span className="material-symbols-outlined">arrow_back</span>
          Back
        </button>
      </div>

      {/* Header */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            {isAdmin ? (mine ? 'My Signals' : 'All Signals') : 'Signal Feed'}
          </h1>
          <p className={styles.subtitle}>
            {isAdmin
              ? (mine ? 'Signals from your own strategies' : 'Signals from all strategies')
              : 'Real-time signals from your active strategies'}
          </p>
        </div>
        <div className={styles.wsStatus}>
          <span
            className={`${styles.wsDot} ${
              feedStatus === 'live'
                ? styles.wsConnected
                : feedStatus === 'idle'
                ? styles.wsIdle
                : styles.wsDisconnected
            }`}
          />
          <span className={styles.wsLabel}>
            {feedStatus === 'live' ? 'Live' : feedStatus === 'idle' ? 'No live data' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* My / All toggle (admin only) */}
      {isAdmin && (
        <div className={styles.viewTabBar}>
          <button
            type="button"
            className={`${styles.viewTabBtn} ${!mine ? styles.viewTabBtnActive : ''}`}
            onClick={() => { navigate('/signals'); setPage(1); }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>lists</span>
            All Signals
          </button>
          <button
            type="button"
            className={`${styles.viewTabBtn} ${mine ? styles.viewTabBtnActive : ''}`}
            onClick={() => { navigate('/signals?mine=true'); setPage(1); }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>person</span>
            My Signals
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'feed' ? styles.tabActive : ''}`}
          onClick={() => setTab('feed')}
        >
          Signal Feed
        </button>
        <button
          type="button"
          className={`${styles.tab} ${tab === 'performance' ? styles.tabActive : ''}`}
          onClick={() => setTab('performance')}
        >
          Performance
        </button>
      </div>

      {tab === 'performance' && <Performance />}

      {tab === 'feed' && (
        <>
      {/* Filters */}
      <div className={styles.filterBar}>
        <div className={styles.filterField}>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="Search symbol..."
            value={filters.symbol}
            onChange={(e) => handleFilterChange('symbol', e.target.value.toUpperCase())}
          />
        </div>
        <div className={styles.filterField}>
          <select
            className={styles.filterSelect}
            value={filters.action_type}
            onChange={(e) => handleFilterChange('action_type', e.target.value)}
          >
            <option value="">All Actions</option>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <select
            className={styles.filterSelect}
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="executed">Executed</option>
          </select>
        </div>
        <div className={styles.filterField}>
          <select
            className={styles.filterSelect}
            value={filters.signal_type}
            onChange={(e) => handleFilterChange('signal_type', e.target.value)}
          >
            <option value="">All Types</option>
            <option value="ENTRY">Entry</option>
            <option value="EXIT">Exit</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className={styles.errorBanner}>
          <span>⚠️ {error.message}</span>
          <button onClick={clearError}>×</button>
        </div>
      )}

      {/* Signal List */}
      {loading && signals.length === 0 ? (
        <div className={styles.spinnerCenter}><Spinner /></div>
      ) : signals.length === 0 ? (
        <div className={styles.emptyState}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, color: 'var(--text-muted)' }}>
            notifications_off
          </span>
          <h3>No signals yet</h3>
          <p>Signals will appear here when your strategy conditions match market data.</p>
        </div>
      ) : (
        <div className={styles.signalList}>
          {signals.map(signal => (
            <SignalCard key={signal.id} signal={signal} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.tableFooter}>
          <span>Showing {signals.length} of {total} signals</span>
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={page === 1 || loading}
              onClick={() => setPage(p => p - 1)}
            >
              {'<'}
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const p = page <= 3 ? i + 1 : page - 2 + i;
              if (p > totalPages || p < 1) return null;
              return (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${page === p ? styles.active : ''}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              );
            })}
            <button
              className={styles.pageBtn}
              disabled={page >= totalPages || loading}
              onClick={() => setPage(p => p + 1)}
            >
              {'>'}
            </button>
          </div>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Signals;
